import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import { contentApi } from '../api/content';

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export interface SeoProps {
  /** Sayfa başlığı. ' | E-Ticaret' son eki otomatik eklenir. */
  title?: string | null;
  /** Meta açıklama (maks 160 karakter önerilir). */
  description?: string | null;
  /** Virgülle ayrılmış anahtar kelimeler. */
  keywords?: string | null;
  /** Tercih edilen kanonik URL. Boşsa mevcut adres kullanılır. */
  canonical?: string | null;
  /** Sosyal medya paylaşım görseli (Open Graph & Twitter Card). */
  image?: string | null;
  /** İçerik türü: website, product, article. */
  type?: 'website' | 'product' | 'article';
  /** Arama motoru indekslemesini kapat (Giriş, sepet, ödeme sayfaları için). */
  noIndex?: boolean;
  /** Site adı (varsayılan: E-Ticaret). */
  siteName?: string;
  /** Ürün sayfası için fiyat bilgisi. */
  price?: {
    amount: number;
    currency?: string;
  };
  /** Ürün stokta mı. */
  inStock?: boolean;
  /** Marka adı. */
  brand?: string | null;
  /** Ürün SKU / Kodu. */
  sku?: string | null;
  /** Puanlama ve yorum sayısı. */
  rating?: {
    average: number;
    count: number;
  };
  /** Ürün değerlendirmeleri (Google zengin sonuçlar & Alışveriş yıldızları için). */
  reviews?: Array<{
    author: string;
    rating: number;
    comment: string;
    title?: string | null;
    createdAt?: string | null;
  }>;
  /** Sayfa kırılım yolu (BreadcrumbList JSON-LD için). */
  breadcrumbs?: BreadcrumbItem[];
  /** Ek özel Schema.org JSON-LD objesi. */
  schemaJson?: Record<string, unknown> | Array<Record<string, unknown>>;
}

const DEFAULT_SITE_NAME = 'Hurmahane';
const DEFAULT_TITLE = 'Hurmahane | Taze Medine Hurmaları ve Hac Malzemeleri';
const DEFAULT_DESCRIPTION =
  'Kutsal topraklardan sofranıza en taze Medine hurmaları, birinci kalite seccadeler, el yapımı kuka ve kehribar tesbihler, hakiki zemzem suyu ve eksiksiz Hac & Umre malzemeleri Hurmahane güvencesiyle.';
const DEFAULT_KEYWORDS =
  'hurmahane, medine hurması, mebrum hurma, acve hurması, seccade, kadife seccade, kuka tesbih, kehribar tesbih, hac malzemeleri, umre malzemeleri, ihram takımı, zemzem suyu, kabe örtüsü kokusu';

export function Seo({
  title,
  description,
  keywords,
  canonical,
  image,
  type = 'website',
  noIndex = false,
  siteName: customSiteName,
  price,
  inStock,
  brand,
  sku,
  rating,
  reviews,
  breadcrumbs,
  schemaJson,
}: SeoProps) {
  const { data: settings } = useQuery({
    queryKey: ['content', 'settings'],
    queryFn: () => contentApi.publicSettings(),
    staleTime: 10 * 60_000,
  });

  const siteName = customSiteName || settings?.['store.name'] || DEFAULT_SITE_NAME;
  const faviconUrl = settings?.['store.favicon_url'];
  const robotsAllowed = settings?.['seo.robots_index'] !== 'false';
  const effectiveNoIndex = noIndex || !robotsAllowed;

  const googleVerification = useMemo(() => {
    const raw = settings?.['seo.google_site_verification']?.trim();
    if (!raw) return null;
    const match = raw.match(/content=["']([^"']+)["']/i);
    return match ? match[1].trim() : raw;
  }, [settings]);

  // Başlık formatlama
  const fullTitle = useMemo(() => {
    const rawTitle = title || settings?.['seo.default_title'] || DEFAULT_TITLE;
    const clean = rawTitle.trim();
    if (clean.endsWith(`| ${siteName}`) || clean.endsWith(`- ${siteName}`)) {
      return clean;
    }
    return `${clean} | ${siteName}`;
  }, [title, settings, siteName]);

  const cleanDescription = (
    description?.trim() ||
    settings?.['seo.default_description'] ||
    settings?.['store.description'] ||
    DEFAULT_DESCRIPTION
  )
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 320);
  const cleanKeywords = keywords?.trim() || DEFAULT_KEYWORDS;

  // URL ve Görsel Mutlak Yolları
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const canonicalUrl = canonical || (typeof window !== 'undefined' ? window.location.origin + window.location.pathname : '');

  const absoluteImage = useMemo(() => {
    if (!image) return null;
    if (image.startsWith('http://') || image.startsWith('https://')) return image;
    if (typeof window !== 'undefined') {
      return `${window.location.origin}${image.startsWith('/') ? '' : '/'}${image}`;
    }
    return image;
  }, [image]);

  // Schema.org JSON-LD Yapılandırılmış Veriler
  const structuredData = useMemo(() => {
    const schemas: Array<Record<string, unknown>> = [];

    // 1. Breadcrumb Schema
    if (breadcrumbs && breadcrumbs.length > 0) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((crumb, idx) => ({
          '@type': 'ListItem',
          position: idx + 1,
          name: crumb.name,
          item: crumb.url.startsWith('http')
            ? crumb.url
            : typeof window !== 'undefined'
              ? `${window.location.origin}${crumb.url.startsWith('/') ? '' : '/'}${crumb.url}`
              : crumb.url,
        })),
      });
    }

    // 2. Product Schema
    if (type === 'product' && title) {
      const productSchema: Record<string, unknown> = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: title,
        description: cleanDescription,
        url: canonicalUrl,
      };

      if (absoluteImage) {
        productSchema.image = [absoluteImage];
      }

      if (brand) {
        productSchema.brand = {
          '@type': 'Brand',
          name: brand,
        };
      }

      if (sku) {
        productSchema.sku = sku;
      }

      if (price) {
        productSchema.offers = {
          '@type': 'Offer',
          url: canonicalUrl,
          priceCurrency: price.currency || 'TRY',
          price: price.amount.toFixed(2),
          availability: inStock
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
          itemCondition: 'https://schema.org/NewCondition',
        };
      }

      if (rating && rating.count > 0) {
        productSchema.aggregateRating = {
          '@type': 'AggregateRating',
          ratingValue: rating.average.toFixed(1),
          reviewCount: rating.count,
          bestRating: '5',
          worstRating: '1',
        };
      }

      if (reviews && reviews.length > 0) {
        productSchema.review = reviews.map((r) => ({
          '@type': 'Review',
          author: {
            '@type': 'Person',
            name: r.author || 'Müşteri',
          },
          ...(r.createdAt ? { datePublished: r.createdAt.split('T')[0] } : {}),
          ...(r.title ? { name: r.title } : {}),
          reviewBody: r.comment,
          reviewRating: {
            '@type': 'Rating',
            ratingValue: String(r.rating),
            bestRating: '5',
            worstRating: '1',
          },
        }));
      }

      schemas.push(productSchema);
    }

    // 3. WebSite Schema (Ana sayfa için)
    if (type === 'website' && !title) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: siteName,
        url: typeof window !== 'undefined' ? window.location.origin : '',
        potentialAction: {
          '@type': 'SearchAction',
          target: `${typeof window !== 'undefined' ? window.location.origin : ''}/urunler?search={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      });
    }

    // 4. Kullanıcı özel schema'sı
    if (schemaJson) {
      if (Array.isArray(schemaJson)) {
        schemas.push(...schemaJson);
      } else {
        schemas.push(schemaJson);
      }
    }

    return schemas;
  }, [breadcrumbs, type, title, cleanDescription, canonicalUrl, absoluteImage, brand, sku, price, inStock, rating, reviews, siteName, schemaJson]);

  return (
    <Helmet>
      {/* 1. Standart Meta Etiketleri */}
      <title>{fullTitle}</title>
      <meta name="description" content={cleanDescription} />
      <meta name="keywords" content={cleanKeywords} />
      <meta name="robots" content={effectiveNoIndex ? 'noindex, nofollow' : 'index, follow'} />
      {googleVerification ? (
        <meta name="google-site-verification" content={googleVerification} />
      ) : null}
      {faviconUrl ? <link rel="icon" href={faviconUrl} /> : null}
      {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}

      {/* 2. Open Graph (Facebook, WhatsApp, LinkedIn) */}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:type" content={type === 'product' ? 'product' : 'website'} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={cleanDescription} />
      {currentUrl ? <meta property="og:url" content={currentUrl} /> : null}
      {absoluteImage ? <meta property="og:image" content={absoluteImage} /> : null}

      {/* Ürün Fiyat & Stok OG Etiketleri */}
      {type === 'product' && price ? (
        <>
          <meta property="product:price:amount" content={price.amount.toFixed(2)} />
          <meta property="product:price:currency" content={price.currency || 'TRY'} />
          <meta
            property="product:availability"
            content={inStock ? 'in stock' : 'out of stock'}
          />
        </>
      ) : null}

      {/* 3. Twitter Card Etiketleri */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={cleanDescription} />
      {absoluteImage ? <meta name="twitter:image" content={absoluteImage} /> : null}

      {/* 4. JSON-LD Yapılandırılmış Veriler */}
      {structuredData.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
